/*
 * Purpose: MCP server connect panel — URL, auth, probe tools via API proxy
 * Last modified: 2026-06-28 | Agent
 * Completeness: 90
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VaultUnlockInline } from '@/components/vault/VaultUnlockInline';
import { ContextualError } from '@/components/errors/ContextualError';
import { useSecretStore } from '@/stores/secretStore';
import { useMcpStore } from '@/stores/mcpStore';
import { probeMcpServer } from '@/services/connectors/mcp/mcpApi';
import {
  getMcpAuthToken,
  getMcpServerUrl,
  isMcpServerConnected,
  MCP_DEFAULT_TOKEN_SECRET,
  MCP_DEFAULT_URL_SECRET,
  slugifyMcpId,
  urlSecretForMcpId,
  tokenSecretForMcpId,
} from '@/services/connectors/mcp/mcpConnection';
import { getMcpPreset } from '@/services/connectors/mcp/mcpPresets';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { Cable, Link2, Plus, Trash2, Unplug } from 'lucide-react';
import { toast } from 'sonner';

interface McpConnectorPanelProps {
  compact?: boolean;
  onConnected?: () => void;
}

export function McpConnectorPanel({ compact = false, onConnected }: McpConnectorPanelProps) {
  const servers = useMcpStore((s) => s.servers);
  const load = useMcpStore((s) => s.load);
  const upsert = useMcpStore((s) => s.upsert);
  const markConnected = useMcpStore((s) => s.markConnected);
  const markError = useMcpStore((s) => s.markError);
  const remove = useMcpStore((s) => s.remove);
  const setSecret = useSecretStore((s) => s.setSecret);
  const isUnlocked = useSecretStore((s) => s.isUnlocked);

  const [searchParams] = useSearchParams();
  const presetId = searchParams.get('preset') ?? undefined;
  const preset = presetId ? getMcpPreset(presetId) : undefined;

  const [activeId, setActiveId] = useState(presetId ?? 'default');
  const [nameDraft, setNameDraft] = useState(preset?.name ?? 'Primary MCP');
  const [urlDraft, setUrlDraft] = useState('');
  const [tokenDraft, setTokenDraft] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (servers.length === 0) {
      upsert({ id: 'default', name: 'Primary MCP' });
    }
  }, [servers.length, upsert]);

  useEffect(() => {
    if (!preset) return;
    const record = upsert({ id: preset.id, name: preset.name });
    setActiveId(record.id);
    setNameDraft(record.name);
    if (preset.serverUrl && !getMcpServerUrl(preset.id)) {
      setUrlDraft(preset.serverUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.id]);

  const active = servers.find((s) => s.id === activeId) ?? servers[0];
  const connected = active ? isMcpServerConnected(active.id) : false;
  const savedUrl = active ? getMcpServerUrl(active.id) : null;

  const ensureUnlocked = (): boolean => {
    if (isUnlocked) return true;
    toast.error('Unlock the vault to save MCP credentials', {
      action: { label: 'Open Setup', onClick: () => { window.location.href = REMEDIATION.setup; } },
    });
    return false;
  };

  const saveCredentials = async () => {
    if (!ensureUnlocked() || !active) return;
    const id = active.id === 'default' ? 'default' : slugifyMcpId(nameDraft || active.name);
    const record = upsert({ id, name: nameDraft.trim() || active.name });
    try {
      if (urlDraft.trim()) await setSecret(record.urlSecret, urlDraft.trim());
      if (tokenDraft.trim()) await setSecret(record.tokenSecret!, tokenDraft.trim());
      setUrlDraft('');
      setTokenDraft('');
      toast.success('MCP credentials saved to vault');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const onConnect = async () => {
    if (!active) return;
    const url = urlDraft.trim() || getMcpServerUrl(active.id);
    if (!url) {
      toast.error('Add an MCP server URL first', {
        action: { label: 'Open vault', onClick: () => { window.location.href = REMEDIATION.setupSecrets; } },
      });
      return;
    }

    if (urlDraft.trim() || tokenDraft.trim()) {
      await saveCredentials();
    }

    setBusy(true);
    try {
      const token = tokenDraft.trim() || getMcpAuthToken(active.id);
      const result = await probeMcpServer(url, token);
      if (!result.ok) {
        markError(active.id, result.error || 'Probe failed');
        toast.error(result.error || 'Could not reach MCP server', {
          action: result.action
            ? { label: result.action.label, onClick: () => { window.location.href = result.action!.href; } }
            : undefined,
        });
        return;
      }

      markConnected(active.id, {
        connectedAt: new Date().toISOString(),
        serverInfo: result.serverInfo ?? undefined,
        toolNames: result.tools?.map((t) => t.name) ?? [],
      });
      toast.success(
        `Connected — ${result.tools?.length ?? 0} tool${result.tools?.length === 1 ? '' : 's'} available`,
      );
      onConnected?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'MCP connect failed';
      if (active) markError(active.id, msg);
      toast.error(msg, {
        action: { label: 'Retry', onClick: () => void onConnect() },
      });
    } finally {
      setBusy(false);
    }
  };

  const onAddServer = () => {
    const name = `MCP ${servers.length + 1}`;
    const id = slugifyMcpId(name);
    upsert({ id, name });
    setActiveId(id);
    setNameDraft(name);
  };

  const urlSecret = active?.urlSecret ?? urlSecretForMcpId(activeId);
  const tokenSecret = active?.tokenSecret ?? tokenSecretForMcpId(activeId);

  return (
    <Card className={compact ? 'border-border/60' : 'border-primary/30'}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <Cable className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">MCP server</CardTitle>
          <Badge variant={connected ? 'default' : 'secondary'}>
            {connected ? 'Connected' : savedUrl ? 'Ready to connect' : 'Needs URL'}
          </Badge>
        </div>
        {!compact && (
          <CardDescription className="text-xs">
            Connect a remote{' '}
            <a
              href="https://modelcontextprotocol.io"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Model Context Protocol
            </a>{' '}
            server (HTTP/SSE). Credentials stay in your vault; tool calls proxy through{' '}
            <code className="text-[10px]">/api/mcp/probe</code>.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <VaultUnlockInline compact />

        {preset && (
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-1.5">
            <p className="text-xs font-medium text-foreground">{preset.name} setup</p>
            <ol className="text-xs text-muted-foreground list-decimal list-inside space-y-0.5">
              {preset.setupSteps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            {preset.requiresCustomUrl && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400">
                No single well-known endpoint for {preset.name} — you'll host or point at your own
                MCP server URL.
              </p>
            )}
          </div>
        )}

        {servers.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {servers.map((s) => (
              <Button
                key={s.id}
                type="button"
                size="sm"
                variant={s.id === activeId ? 'default' : 'outline'}
                onClick={() => {
                  setActiveId(s.id);
                  setNameDraft(s.name);
                }}
              >
                {s.name}
              </Button>
            ))}
            <Button type="button" size="sm" variant="ghost" onClick={onAddServer}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add
            </Button>
          </div>
        )}

        {active?.lastError && (
          <ContextualError
            message={active.lastError}
            action={{ label: 'Retry connect', onClick: () => void onConnect() }}
            secondaryAction={{ label: 'Vault secrets', href: REMEDIATION.setupSecrets }}
          />
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-xs">Display name</Label>
            <Input
              className="mt-1 text-xs"
              value={nameDraft || active?.name || ''}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder="e.g. Upstash, Stripe, custom tools"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">{urlSecret}</Label>
            <Input
              className="mt-1 text-xs font-mono"
              placeholder={savedUrl ? maskUrl(savedUrl) : 'https://your-mcp-host/mcp'}
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">{tokenSecret} (optional)</Label>
            <Input
              type="password"
              className="mt-1 text-xs font-mono"
              placeholder="Bearer token if required"
              value={tokenDraft}
              onChange={(e) => setTokenDraft(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => void saveCredentials()}>
            Save to vault
          </Button>
          <Button type="button" size="sm" onClick={() => void onConnect()} disabled={busy}>
            <Link2 className="h-3.5 w-3.5 mr-1" />
            {busy ? 'Connecting…' : connected ? 'Reconnect & refresh tools' : 'Connect MCP'}
          </Button>
          {active && active.id !== 'default' && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-destructive"
              onClick={() => {
                remove(active.id);
                setActiveId('default');
              }}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Remove
            </Button>
          )}
        </div>

        {connected && active?.toolNames && active.toolNames.length > 0 && (
          <div className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-2">
            <p className="text-xs font-medium text-foreground">
              Tools ({active.toolNames.length})
              {active.serverInfo?.name ? ` — ${active.serverInfo.name}` : ''}
            </p>
            <ul className="text-xs text-muted-foreground space-y-1 max-h-40 overflow-y-auto">
              {active.toolNames.map((tool) => (
                <li key={tool} className="font-mono">
                  {tool}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!connected && !compact && (
          <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
            <Unplug className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            Stdio MCP (local CLI) is not supported in the browser — use an HTTP/SSE remote server or
            a tunnel (e.g. Cloudflare, ngrok) pointing at your MCP endpoint.
          </p>
        )}

        {!compact && (
          <p className="text-[11px] text-muted-foreground">
            Default secrets: <code className="font-mono">{MCP_DEFAULT_URL_SECRET}</code>,{' '}
            <code className="font-mono">{MCP_DEFAULT_TOKEN_SECRET}</code>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function maskUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname.length > 12 ? `${u.pathname.slice(0, 8)}…` : u.pathname}`;
  } catch {
    return '•••••• (saved)';
  }
}

export default McpConnectorPanel;
